import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import api from "../services/api";
import { toast } from "react-toastify";

const InviteHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("joining");
  const hasJoined = useRef(false);

  const token = searchParams.get("token");

  const joinMutation = useMutation({
    mutationFn: (inviteToken) => api.joinLiveRoom({ token: inviteToken }),

    onSuccess: (response) => {
      const successMessage = response?.message || "Successfully joined the Live Room!";

      setStatus("success");
      toast.success(successMessage);

      setTimeout(() => {
        navigate("/messages", { replace: true });
      }, 1500);
    },

    onError: (error) => {
      const errorMessage = error?.response?.data?.message || "Failed to join the room. Please try again.";
      toast.error(errorMessage);
      setStatus("error");
      setTimeout(() => {
        navigate("/messages", { replace: true });
      }, 1500);
    }
  });

  useEffect(() => {
    if (!token) {
      toast.error("No invite token found");
      navigate("/messages", { replace: true });
      return;
    }

    // Prevent duplicate API call
    if (hasJoined.current) return;

    hasJoined.current = true;
    joinMutation.mutate(token);
  }, [token, navigate]);

  // Loading / Status UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1F3A60] to-[#0F2A4A] flex items-center justify-center p-4">
      <div className="text-center">
        <div className="mb-8">
          {status === "joining" && (
            <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
          )}
          {status === "success" && (
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {status === "error" && (
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6h12v12" />
              </svg>
            </div>
          )}
        </div>

        <h1 className="text-2xl font-semibold text-white mb-2">
          {status === "joining" && "Joining Live Room..."}
          {status === "success" && "Welcome!"}
          {status === "error" && "Join Failed"}
        </h1>

        <p className="text-white/70 max-w-xs mx-auto">
          {status === "joining" && "Please wait while we add you to the room"}
          {status === "success" && "Redirecting to Messages..."}
          {status === "error" && "Redirecting to Messages..."}
        </p>

        {status === "joining" && (
          <div className="mt-8 text-white/50 text-sm">This may take a few seconds</div>
        )}
      </div>
    </div>
  );
};

export default InviteHandler;